import { autorun } from "mobx";
import {
  PurchaseLinkPriceOption,
  PurchaseVariantSelectionType,
  SystemItemTypes,
  SellerReturnPolicyType,
  SellerTermsAndConditionsType,
} from "../enums";
import {
  IAPIPromotionRelatedGridModel,
  IAPIPurchaseLinkGridModel,
  IAPIPurchaselinkModel,
  IAPIPurchaseLinkOrdersGridModel,
} from "../interfaces";
import { SellerModel } from "./SellerModels";
import { PricingPageInputModel } from "./PriceInputs.model";
import { ItemDetailPageModel } from "./ItemDetailPageModels";
import { ProductAttributeInputModel } from "./ProductModel";
import { ItemDonationModel } from "./ItemDonationModel";
import { MediaModel } from "./MediaModel";
import { RelatedSectionModel } from "./DonationAndItemModel";
import {
  MerchantAccount,
  PaymentMethodDetails,
} from "./MerchantApplicationInputModel";
import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  IOption,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  getVariantOptions,
  priceOptions,
} from "../styles/PurchaseLinkPopup.styles";
import PricingPageService from "../services/PricingPageService";
import { PaymentModuleUtility } from "../utils/PaymentModuleUtility";

export const linkDomainOptions: IOption[] = [
  { label: "pay-checkout.co", value: "pay-checkout.co" },
  { label: "secure-payment.us", value: "secure-payment.us" },
  { label: "secure-checkout.us", value: "secure-checkout.us" },
];

export class PurchaseLinkDetailPageModel {
  selectedSharingType: string;
  createdBy: string;
  createdDate: Date;
  isLinkEditing: boolean;
  linkDomain: string;
  linkPath: string;
  promoCodes: PromotionRelatedGridModel[];
  orders: PurchaseLinkOrdersGridModel[];
  name: string;

  constructor(data?: Partial<PurchaseLinkDetailPageModel>) {
    this.selectedSharingType = data?.selectedSharingType || "Link";
    this.createdBy = data?.createdBy || "";
    this.createdDate = new Date(data?.createdDate || "");
    this.promoCodes = (data?.promoCodes || []).map(
      (promotion) => new PromotionRelatedGridModel(promotion),
    );
    this.orders = (data?.orders || []).map(
      (order) => new PurchaseLinkOrdersGridModel(order),
    );
    this.isLinkEditing = data?.isLinkEditing || false;
    this.linkDomain = data?.linkDomain || linkDomainOptions[0].value;
    this.linkPath = data?.linkPath || "";
    this.name = data?.name || "";
  }
}

export class PurchaseLinkModel {
  id: string;
  isNew: boolean;
  createdDate: Date;
  isOpen: boolean;
  itemType: keyof typeof SystemItemTypes;
  variantsType: keyof typeof PurchaseVariantSelectionType;
  promotionValue: string;
  priceType: keyof typeof PurchaseLinkPriceOption;
  chooseQuantity: boolean;
  includeRelatedItems: boolean;
  selectedMerchantAccount: MerchantAccount;
  selectedSeller: SellerModel;
  freeTrial: boolean;
  freeTrialDays: number;
  itemDetails: ItemDetailPageModel | null;
  pricingInputs: PricingPageInputModel[] | null;
  relatedSectionData: RelatedSectionModel | null;
  variants: ProductAttributeInputModel | null;
  selectedVariantIds: string[];
  selectedPriceIds: string[];
  itemId: string;
  isTrackInventory: boolean;
  showOriginalPrice: boolean;
  surchargeEnabled: boolean;
  surchargePercentage: string;
  surchargeAskCustomerFirst: boolean;
  sellerTermsAndConditions: keyof typeof SellerTermsAndConditionsType;
  sellerReturnPolicy: keyof typeof SellerReturnPolicyType;
  purchaseBaseURL: string;
  link: string;
  errors: InputErrorModel<PurchaseLinkModel>[];
  constructor(data?: Partial<PurchaseLinkModel>) {
    this.id = data?.id || "";
    this.isNew = data?.isNew ?? true;
    this.showOriginalPrice = data?.showOriginalPrice || false;
    this.isOpen = data?.isOpen || true;
    this.itemType = data?.itemType || SystemItemTypes.Product;
    this.variantsType =
      data?.variantsType ||
      (getVariantOptions(false)[1]
        .value as keyof typeof PurchaseVariantSelectionType);
    this.promotionValue = data?.promotionValue || "None";
    this.priceType =
      data?.priceType ||
      (priceOptions[1].value as keyof typeof PurchaseLinkPriceOption);
    this.chooseQuantity = data?.chooseQuantity || false;
    this.includeRelatedItems = data?.includeRelatedItems || false;
    this.selectedMerchantAccount = new MerchantAccount(
      data?.selectedMerchantAccount,
    );
    this.selectedSeller = new SellerModel(data?.selectedSeller);
    this.freeTrial = data?.freeTrial || false;
    this.freeTrialDays = data?.freeTrialDays || 0;
    this.itemDetails = data?.itemDetails
      ? new ItemDetailPageModel(data?.itemDetails)
      : null;
    this.pricingInputs = data?.pricingInputs
      ? data?.pricingInputs.map((price) => new PricingPageInputModel(price))
      : null;
    this.relatedSectionData = data?.relatedSectionData
      ? new RelatedSectionModel(data?.relatedSectionData)
      : null;
    this.variants = data?.variants
      ? new ProductAttributeInputModel(data.variants)
      : null;
    this.selectedVariantIds = data?.selectedVariantIds || [];
    this.selectedPriceIds = data?.selectedPriceIds || [];
    this.itemId = data?.itemId || "";
    this.isTrackInventory = data?.isTrackInventory || false;
    this.surchargeEnabled = data?.surchargeEnabled || false;
    // No default: leave empty so the user can clear it; the 0.1%-4% range is enforced on submit.
    this.surchargePercentage = data?.surchargePercentage ?? "";
    this.surchargeAskCustomerFirst = data?.surchargeAskCustomerFirst || false;
    this.sellerTermsAndConditions =
      data?.sellerTermsAndConditions || "DoNotDisplay";
    this.sellerReturnPolicy = data?.sellerReturnPolicy || "DoNotDisplay";
    this.errors = data?.errors || [];
    this.createdDate = new Date(data?.createdDate || "");
    this.purchaseBaseURL = PaymentModuleUtility.removeLastBackslash(
      data?.purchaseBaseURL,
    );
    this.link = "";
    if (this.purchaseBaseURL) {
      this.link =
        this.purchaseBaseURL + "/app/purchase/purchase-link/" + this.id;
    }

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof PurchaseLinkModel) {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  /**
   * Rate this link holds, as typed or as loaded, whether or not the surcharge
   * is currently switched on. Use it to tell "no rate stored" from "rate
   * stored but surcharge off".
   */
  get surchargePercentageValue(): number {
    const percentage = parseFloat(
      this.surchargePercentage?.toString().replace(/,/g, "") || "0",
    );
    return isNaN(percentage) ? 0 : percentage;
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PurchaseLinkModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.selectedMerchantAccount.name,
      "selectedMerchantAccount",
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.promotionValue,
      "promotionValue",
      newErrors,
    );
    CoreInputModelUtil.validateTextField(
      this.itemDetails?.name || "",
      "name" as keyof PurchaseLinkModel,
      newErrors,
    );

    CoreInputModelUtil.validateTextField(
      this.selectedSeller.id,
      "selectedSeller",
      newErrors,
    );
    if (this.itemType === SystemItemTypes.Product) {
      CoreInputModelUtil.validateTextField(
        this.variantsType || "",
        "variantsType",
        newErrors,
      );
    }
    if (
      this.itemType === SystemItemTypes.Service ||
      this.itemType === SystemItemTypes.Membership
    ) {
      CoreInputModelUtil.validateTextField(
        this.priceType || "",
        "priceType",
        newErrors,
      );
    }
    if (this.itemDetails?.return) {
      CoreInputModelUtil.validateNumber(
        this.itemDetails.returnDays,
        "returnDays" as keyof PurchaseLinkModel,
        newErrors,
      );
    }
    if (this.freeTrial) {
      CoreInputModelUtil.validateNumber(
        this.freeTrialDays,
        "freeTrialDays" as keyof PurchaseLinkModel,
        newErrors,
      );
    }

    if (this.surchargeEnabled) {
      const {
        minSurchargePercentage,
        maxSurchargePercentage,
        surchargeRangeMessage,
      } = PaymentMethodDetails;
      const percentage = this.surchargePercentageValue;
      if (
        percentage < minSurchargePercentage ||
        percentage > maxSurchargePercentage
      ) {
        newErrors.push(
          new InputErrorModel<PurchaseLinkModel>({
            field: "surchargePercentage",
            message: surchargeRangeMessage,
          }),
        );
      }
    }

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIModel(): IAPIPurchaselinkModel {
    return {
      id: this.id,
      name: this.itemDetails?.name || "",
      isActive: this.itemDetails?.isActive,
      accountId: AuthService.authData.accountId,
      priceOption: this.priceType,
      itemId: this.itemId,
      itemType: this.itemType,
      priceIds: this.selectedPriceIds,
      promoId: this.promotionValue && null,
      options: {
        tax: this.itemDetails?.tax || false,
        /* Kept in step with the surcharge fields below, so anything still
           reading the plain boolean keeps working. */
        surcharge: this.surchargeEnabled,
        customerCanChooseQuantity: this.chooseQuantity,
        showOriginalPrice: this.showOriginalPrice,
        return: this.itemDetails?.return || false,
        returnDays: this.itemDetails?.returnDays || 0,
        trial: this.freeTrial,
        trialDays: this.freeTrialDays,
        includeRelatedItems: this.includeRelatedItems,
      },
      surchargeEnabled: this.surchargeEnabled,
      surchargePercentage: this.surchargeEnabled
        ? parseFloat(
            this.surchargePercentage?.toString().replace(/,/g, "") || "0",
          )
        : 0,
      surchargeAskCustomerFirst: this.surchargeAskCustomerFirst,
      sellerTermsAndConditionsType: this.sellerTermsAndConditions,
      sellerReturnPolicyType: this.sellerReturnPolicy,
      variantSelectionType: this.variantsType as PurchaseVariantSelectionType,
      variantIds: this.selectedVariantIds,
      merchantAccount: this.selectedMerchantAccount.createAPIModel2(),
      seller: this.selectedSeller.createAPIModel(),
    } as IAPIPurchaselinkModel;
  }

  createAPIModelForNameChange(name: string): IAPIPurchaselinkModel {
    return {
      ...this.createAPIModel(),
      name: name,
    } as IAPIPurchaselinkModel;
  }

  static deserializeAPIResponse(
    data: IAPIPurchaselinkModel,
  ): PurchaseLinkModel {
    const donationData = data.donation
      ? ItemDonationModel.deserializeAPIModel(data.donation)
      : null;
    return new PurchaseLinkModel({
      purchaseBaseURL: data.purchaseBaseURL,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      itemDetails: new ItemDetailPageModel({
        name: data.name,
        isActive: data.isActive,
        tax: data.options.tax,
        surcharge: data.options.surcharge,
        return: data.options.return,
        returnDays: data.options.returnDays,
        hasVariants: data.itemDetails?.hasVariants || false,
        medias: data.donation
          ? donationData?.detailSection.medias
          : data.itemDetails?.medias.map((media) =>
              MediaModel.deserializeAPIModel(media),
            ),
      }),
      id: data.id,
      priceType: data.priceOption as keyof typeof PurchaseLinkPriceOption,
      itemId: data.itemId,
      itemType: data.itemType,
      selectedPriceIds: data.priceIds,
      promotionValue: data.promoId ?? "",
      freeTrial: data.options.trial,
      showOriginalPrice: data.options.showOriginalPrice,
      variants: data.itemDetails?.hasVariants
        ? data.itemDetails?.variants &&
          ProductAttributeInputModel.deserializeAPI(
            data.itemDetails.variants,
            data.itemDetails.isTrackInventory || false,
          )
        : null,
      isTrackInventory: data.itemDetails?.isTrackInventory,
      freeTrialDays: data.options.trialDays,
      chooseQuantity: data.options.customerCanChooseQuantity,
      includeRelatedItems: data.options.includeRelatedItems,
      surchargeEnabled: data.surchargeEnabled ?? false,
      surchargePercentage:
        data.surchargePercentage != null
          ? data.surchargePercentage.toString()
          : "",
      surchargeAskCustomerFirst: data.surchargeAskCustomerFirst ?? false,
      sellerTermsAndConditions:
        data.sellerTermsAndConditionsType || "DoNotDisplay",
      sellerReturnPolicy: data.sellerReturnPolicyType || "DoNotDisplay",
      variantsType:
        data.variantSelectionType as keyof typeof PurchaseVariantSelectionType,
      selectedVariantIds: data.variantIds,
      selectedMerchantAccount: data.merchantAccount
        ? MerchantAccount.deserializeAPIModel2(data.merchantAccount)
        : new MerchantAccount(),
      selectedSeller: data.seller
        ? new SellerModel(data.seller)
        : new SellerModel(),
      pricingInputs: data.itemDetails?.prices
        ? PricingPageService.deserializeAPIModel(data.itemDetails.prices || [])
        : null,
      relatedSectionData: data.itemDetails?.relatedItems
        ? RelatedSectionModel.deserializeAPIModel(data.itemDetails.relatedItems)
        : donationData &&
          donationData.includedItems &&
          new RelatedSectionModel({
            includedItems: donationData.includedItems[0],
            recommendedItems: donationData.recommendedItems,
          }),
    });
  }
}

export class PurchaseLinkGridModel {
  id: string;
  status: string;
  name: string;
  lastPurchase: string;
  createdDate: string;
  totalViews: number;
  purchaseBaseURL: string;
  isDefault: boolean;

  constructor(data?: Partial<PurchaseLinkGridModel>) {
    this.id = data?.id || "";
    this.status = data?.status || "";
    this.name = data?.name || "";
    this.lastPurchase = data?.lastPurchase || "";
    this.createdDate = data?.createdDate || "";
    this.totalViews = data?.totalViews || 0;
    this.isDefault = data?.isDefault || false;
    this.purchaseBaseURL = PaymentModuleUtility.removeLastBackslash(
      data?.purchaseBaseURL,
    );
  }

  static deserializeAPIModel(
    data: IAPIPurchaseLinkGridModel,
    isFirst: boolean,
  ): PurchaseLinkGridModel {
    const mappedData: Partial<PurchaseLinkGridModel> = {
      id: data.id,
      status: data.isActive ? "Active" : "Inactive",
      name: data.name,
      lastPurchase: data.lastPurchase
        ? new Date(data.lastPurchase).toLocaleDateString()
        : "Never",
      createdDate: new Date(data.createdDate).toLocaleDateString(),
      totalViews: data.totalViews,
      purchaseBaseURL: data.purchaseBaseURL,
      isDefault: isFirst,
    };

    return new PurchaseLinkGridModel(mappedData);
  }
}

export class PromotionRelatedGridModel {
  id: string;
  type: string;
  name: string;
  discount: string;
  limitations: string;
  promoCode: number;

  constructor(data?: Partial<PromotionRelatedGridModel>) {
    this.id = data?.id || "";
    this.type = data?.type || "";
    this.name = data?.name || "";
    this.discount = data?.discount || "";
    this.limitations = data?.limitations || "";
    this.promoCode = data?.promoCode || 0;
  }

  static deserializeAPIModel(
    data: IAPIPromotionRelatedGridModel,
  ): PromotionRelatedGridModel {
    const mappedData: Partial<PromotionRelatedGridModel> = {
      id: data.id,
      name: data.name,
      type: data.type,
      discount: data.discount,
      limitations: data.limitations,
      promoCode: data.promoCode,
    };

    return new PromotionRelatedGridModel(mappedData);
  }
}

export class PurchaseLinkOrdersGridModel {
  id: string;
  status: string;
  description: string;
  created: string;
  items: number;
  amount: string;
  recurring: string;

  constructor(data?: Partial<PurchaseLinkOrdersGridModel>) {
    this.id = data?.id || "";
    this.status = data?.status || "";
    this.description = data?.description || "";
    this.created = data?.created || "";
    this.items = data?.items || 0;
    this.amount = data?.amount || "";
    this.recurring = data?.recurring || "";
  }

  static deserializeAPIModel(
    data: IAPIPurchaseLinkOrdersGridModel,
  ): PurchaseLinkOrdersGridModel {
    const mappedData: Partial<PurchaseLinkOrdersGridModel> = {
      id: data.id,
      status: data.status,
      created: data.created,
      description: data.description,
      items: data.items,
      amount: data.amount,
      recurring: data.recurring,
    };

    return new PurchaseLinkOrdersGridModel(mappedData);
  }
}
